import PropTypes from 'prop-types';
import {
	Drawer,
	Box,
	Typography,
	IconButton,
	useMediaQuery,
	useTheme,
	CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AMFIForm from './AMFIForm';

/**
 * AMFIDrawer component
 * A responsive drawer for creating/editing AMFI items
 * Appears from right side on desktop and bottom on mobile
 */
const AMFIDrawer = ({
	open,
	onClose,
	type = 'attribute',
	item = null,
	categories = [],
	onSubmit,
	onCancel,
	isSubmitting = false,
}) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	// Generate title based on type and mode (create/edit)
	const title = item
		? `Edit ${type.charAt(0).toUpperCase() + type.slice(1)}`
		: `New ${type.charAt(0).toUpperCase() + type.slice(1)}`;

	return (
		<Drawer
			anchor={isMobile ? 'bottom' : 'right'}
			open={open}
			onClose={onClose}
			PaperProps={{
				sx: {
					width: isMobile ? '100%' : '500px',
					maxHeight: isMobile ? '90vh' : '100%',
					borderRadius: isMobile ? '16px 16px 0 0' : '0',
					padding: 0,
					zIndex: 1300,
				},
			}}
		>
			<Box
				sx={{
					p: 0,
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				{/* Drawer Header */}
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						borderBottom: `1px solid ${theme.palette.divider}`,
						p: 2,
					}}
				>
					<Typography variant='h6'>{title}</Typography>
					<IconButton onClick={onClose} edge='end' aria-label='close'>
						<CloseIcon />
					</IconButton>
				</Box>

				{/* Form Container */}
				<Box
					sx={{
						p: 2,
						flexGrow: 1,
						overflowY: 'auto',
					}}
				>
					<AMFIForm
						type={type}
						item={item}
						categories={categories}
						onSubmit={onSubmit}
						onCancel={onCancel || onClose}
					/>

					{/* Loading indicator */}
					{isSubmitting && (
						<Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
							<CircularProgress size={24} />
						</Box>
					)}
				</Box>
			</Box>
		</Drawer>
	);
};

AMFIDrawer.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	type: PropTypes.oneOf(['attribute', 'metric', 'fact', 'insight']),
	item: PropTypes.object,
	categories: PropTypes.array,
	onSubmit: PropTypes.func.isRequired,
	onCancel: PropTypes.func,
	isSubmitting: PropTypes.bool,
};

export default AMFIDrawer;
